import Button from "Components/Button";
import { OrderCartItem, useOrderCartStore } from "../useOrderCartStore";
import Scaffold from "Components/Scaffold";
import { Card } from "Components/Card";
import { Separator } from "Components/Separator";
import { formatCurrency } from "utils/formatNumber";
import { ellipse } from "utils/ellipse";
import { Icon } from "@iconify-icon/react";
import { Counter } from "../Components/Counter";
import { Dropdown, MenuProps, Modal } from "antd";
import { ManualOrderCustomerSelection } from "./ManualOrderCustomerSelection";
import { useState } from "react";
import { Customer } from "hooks/CustomerManagement/useGetCustomerQuery";
import {
  CreateManualOrderResponse,
  useCreateOrder,
} from "hooks/OrderManagement/useOrders";
import { OrderReceipt } from "./Receipt";
import { Branch } from "hooks/BranchManagement/useGetBranchesQuery";
import { ManualOrderBranchSelection } from "./ManualOrderBranchSelection";

const empty = require("assets/empty.png");

const { confirm } = Modal;

const confirmModalProps = {
  okButtonProps: {
    className: "bg-orange hover:!bg-primary/60 text-white",
  },
  cancelButtonProps: {
    className:
      "border-orange text-orange hover:!border-orange hover:!bg-primary-light hover:!text-orange",
  },
};

const CartItem = ({ item }: { item: OrderCartItem }) => {
  const setCart = useOrderCartStore((state) => state.setCart);
  const decreaseCartItem = useOrderCartStore((state) => state.decreaseCartItem);
  const removeCartItem = useOrderCartStore((state) => state.removeCartItem);

  const showConfirm = () => {
    confirm({
      // icon: <ExclamationCircleOutlined />,
      ...confirmModalProps,
      content: <div>Are you sure you want to delete this item?</div>,
      onOk() {
        removeCartItem(item.id);
      },
    });
  };

  const totalPrice = (item.bulkPrice * (item.count || 1)) / 100;

  return (
    <div className="flex justify-between items-center text-base font-medium p-4 gap-x-2">
      <div className="size-14">
        <img
          src={item.images[0]}
          title={item.name}
          alt={item.name}
          className="object-contain size-14"
        />
      </div>
      <div className="flex-1 flex flex-col gap-2">
        <div className="flex justify-between">
          <p>{ellipse(item.name)}</p>
          <div className="">
            <Icon
              icon="hugeicons:delete-02"
              width={20}
              onClick={showConfirm}
              className="cursor-pointer"
            />
          </div>
        </div>
        <div className="flex justify-between">
          <div className="">
            <Counter
              onIncrement={() => setCart(item)}
              onDecrement={() => {
                if (item.count === 1) {
                  return showConfirm();
                }
                decreaseCartItem(item.id);
              }}
              count={item.count}
            />
          </div>
          <div className="text-grey font-bold">
            {formatCurrency(totalPrice)}
          </div>
        </div>
      </div>
      {/* <Modal
        title={`Delete item`}
        open={true}
        // onCancel={closeDeleteModal}
        footer={null}>
        <h3 className="text-[16px] maanrope mb-4">
          Confirm that you'd like to delete the selected branches
        </h3>

        <Button
          label="Delete"
          className="text-white bg-[#f00] p-[12px] font-bold rounded-md ease-in-out duration-300"
          onClick={() => {
            // deleteBranchSelected();
          }}
        />
      </Modal> */}
    </div>
  );
};

const CartItems = () => {
  const cartItems = useOrderCartStore((state) => state.cart);

  const isEmpty = cartItems.length === 0;

  if (isEmpty) {
    return (
      <div className="h-full grid place-content-center">
        <img src={empty} alt="empty" className="flex mx-auto" />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {cartItems.map((item, index) => (
        <>
          <CartItem item={item} key={item.id} />
          {index !== cartItems.length - 1 && <Separator />}
        </>
      ))}
    </div>
  );
};

export const ManualOrderCart = () => {
  const cartItems = useOrderCartStore((state) => state.cart);
  const clearCart = useOrderCartStore((state) => state.clearCart);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showBranch, setShowBranch] = useState(false);

  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | null
  >(null);
  const [orderSuccessResponse, setOrderSuccessResponse] =
    useState<CreateManualOrderResponse | null>(null);

  const {
    createOrderQuoteMutationAsync,
    createOrderQuoteLoading,
    createOrderMutation,
    createOrderLoading,
  } = useCreateOrder();

  const cartItemsLength = cartItems.length;
  const hasItemsInCart = cartItems.length > 0;

  const showConfirm = () => {
    confirm({
      ...confirmModalProps,
      content: <div>Are you sure you want to clear your cart?</div>,
      onOk() {
        clearCart();
      },
    });
  };

  const total = cartItems.reduce((acc, item) => {
    return acc + (item.bulkPrice / 100) * item.count;
  }, 0);

  const items: MenuProps["items"] = [
    {
      label: <div>Cash</div>,
      key: "0",
      onClick: () => {
        setSelectedPaymentMethod("Cash");
      },
    },
    {
      type: "divider",
    },
    {
      label: <div>Debit Card</div>,
      key: "1",
      onClick: () => {
        setSelectedPaymentMethod("Debit Card");
      },
    },
    {
      type: "divider",
    },
    {
      label: <div>Bank Transfer</div>,
      key: "3",
      onClick: () => {
        setSelectedPaymentMethod("Bank Transfer");
      },
    },
  ];

  const isButtonEnabled = Boolean(
    selectedCustomer && selectedPaymentMethod && hasItemsInCart
  );

  const onSubmit = async () => {
    if (!selectedPaymentMethod || !selectedCustomer) {
      return alert("No payment method selected");
    }

    const orderProducts = cartItems.map((item) => ({
      productId: item.id,
      quantity: item.count,
    }));

    const res = await createOrderQuoteMutationAsync({
      products: orderProducts,
      type: "bulk",
    });
    console.log("🚀 ~ onSubmit ~ res:", res);

    if (res.totalAmount !== total)
      return alert(
        "Amount not correct, refresh the page to get the latest prices of the product"
      );

    createOrderMutation(
      {
        type: "bulk",
        customerId: selectedCustomer?.id || "",
        customerName:
          selectedCustomer?.firstName + " " + selectedCustomer?.lastName,
        customerPhone: selectedCustomer?.phoneNumber,
        paymentMethod: selectedPaymentMethod,
        orderProducts,
        branch: selectedBranch?.id || "",
      },
      {
        onSuccess: (res) => {
          setOrderSuccessResponse(res);
          setShowBranch(false);
          setShowSuccessModal(true);
          resetOrderProcess();
        },
      }
    );
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    resetOrderProcess();
  };

  const resetOrderProcess = () => {
    setSelectedCustomer(null);
    setSelectedPaymentMethod(null);
    clearCart();
  };

  return (
    <div className="w-full">
      <div className="flex flex-col gap-y-4">
        <Card className="text-base font-medium flex justify-between items-center min-h-16">
          <h6 className="">
            Cart {hasItemsInCart ? `(${cartItemsLength})` : ""}
          </h6>
          {hasItemsInCart && (
            <div className="">
              <Button
                label={
                  <div className="flex gap-x-1 items-center">
                    <Icon icon="hugeicons:delete-02" width={20} />
                    Clear cart
                  </div>
                }
                onClick={showConfirm}
                className="bg-primary-light text-[#414141] p-3 text-sm font-medium"
              />
            </div>
          )}
        </Card>

        <Card className="text-base font-medium h-[29rem] p-0 overflow-y-auto">
          <CartItems />
        </Card>

        <Scaffold left="Order Summary" className="p-0">
          <div className="">
            <div className="p-3">
              <div className="flex items-center justify-between mb-3">
                <h3>Payment Method</h3>

                <Dropdown menu={{ items }} trigger={["click"]}>
                  <div
                    onClick={(e) => e.preventDefault()}
                    className="flex items-center justify-center">
                    {selectedPaymentMethod || "Select"}
                    <Icon
                      icon="iconamoon:arrow-down-2-duotone"
                      width={20}
                      color="#474747"
                    />
                  </div>
                </Dropdown>
              </div>
              <div className="flex items-center justify-between mb-3">
                <h3>Sub Total</h3>
                <span>{formatCurrency(total)}</span>
              </div>
              <div className="flex items-center justify-between mb-3">
                <h3>Total</h3>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>

            <Separator />
            <div className="p-3 flex justify-between">
              <h3>Customer</h3>
              <ManualOrderCustomerSelection
                selectedCustomer={selectedCustomer}
                setSelectedCustomer={setSelectedCustomer}
              />
            </div>
            <Separator />

            <div className="p-3">
              <Button
                label="Confirm Payment"
                disabled={!isButtonEnabled}
                onClick={() => setShowBranch(true)}
                isLoading={createOrderLoading || createOrderQuoteLoading}
              />
            </div>

            <Modal
              open={showSuccessModal}
              onCancel={handleSuccessModalClose}
              closable={false}
              footer={null}>
              <div className="text-center grid gap-y-6 py-5">
                <div className="flex justify-center items-center mb-1">
                  <Icon icon="fxemoji:star" width={92} />
                </div>

                <div className="space-y-5">
                  <h2 className="text-xl font-medium">Order Completed</h2>
                  <p className="text-primary-foreground text-lg">
                    The order has been successfully placed for the customer
                    in-store.
                  </p>
                </div>

                <div className="space-y-2">
                  <Button
                    label="Print Receipt"
                    onClick={() => setShowReceiptModal(true)}></Button>
                  <Button
                    label="Create Another Order"
                    onClick={handleSuccessModalClose}
                    className="bg-[#E8E8E8] text-[#2E2E2E]"></Button>
                </div>
              </div>
            </Modal>

            <ManualOrderBranchSelection
              isOpen={showBranch}
              onClose={() => setShowBranch(false)}
              branch={selectedBranch}
              setBranch={setSelectedBranch}
              confirmButtonProps={{
                isLoading: createOrderLoading || createOrderQuoteLoading,
                onClick: () => {
                  onSubmit();
                },
              }}
            />

            <Modal
              open={showReceiptModal}
              onCancel={() => setShowReceiptModal(false)}
              closable={false}
              footer={null}
              className="p-0 print:invisible !w-[350px]">
              <OrderReceipt
                order={
                  orderSuccessResponse
                    ? {
                        ...(orderSuccessResponse || {}),
                      }
                    : null
                }
              />
            </Modal>
          </div>
        </Scaffold>
      </div>
    </div>
  );
};
